import React from 'react';
import {StyleSheet, View} from 'react-native';
import {Card, makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react';

import {Icon} from '../../../../components/Icon';
import {CartSummarySkeleton} from '../../../../components/skeleton';
import Text from '../../../../components/themed/Text';
import ActivityIndicator from '../../../../components/ui-controls/ActivityIndicator';
import {useStores} from '../../../../stores/use-stores';
import theme from '../../../../style/theme';
import {OrderLine} from '../../../../transport/apiServiceResponse';
import {toAUD, toAUDAbs} from '../../../../utils/functions';
import {
  calculateSubtotal,
  getChargeLineAmount,
  getChargeLines,
  isUncountedCouponLine,
  isUncountedOfferAdjustment,
  sumUncountedDiscounts,
} from '../../../../utils/orderCalculations';

// Extracted "as-is" from the original CheckoutScreen.tsx for
// potential reuse in Gift Cards Store but at the end it didn't
// happen

/**
 * Inline warning banner for expired offers (DIS-643).
 * Displays when cartStore.expiredOfferDetected is true.
 */
const ExpiredOfferBanner = ({narrative}: {narrative: string}) => {
  const bannerStyles = StyleSheet.create({
    container: {
      backgroundColor: '#FEF3C7',
      borderLeftWidth: 4,
      borderLeftColor: '#F59E0B',
      borderRadius: 4,
      padding: 12,
      marginVertical: 8,
      flexDirection: 'row',
    },
    iconContainer: {
      marginRight: 12,
      paddingTop: 2,
    },
    content: {
      flex: 1,
    },
    title: {
      fontWeight: '600',
      fontSize: 14,
      color: '#92400E',
      marginBottom: 8,
    },
    body: {
      fontSize: 14,
      color: '#92400E',
      lineHeight: 20,
    },
  });

  return (
    <View style={bannerStyles.container} testID="expired-offer-banner">
      <View style={bannerStyles.iconContainer}>
        <Icon name="triangle-exclamation" size={16} color="#F59E0B" />
      </View>
      <View style={bannerStyles.content}>
        <Text style={bannerStyles.title}>Offer No Longer Valid</Text>
        <Text style={bannerStyles.body}>
          The offer "{narrative}" has expired and can no longer be applied to your order.
        </Text>
        <Text style={[bannerStyles.body, {marginTop: 8}]}>
          It will be removed when you proceed to checkout.
        </Text>
      </View>
    </View>
  );
};

type OrderTotalsProps = {
  children?: React.ReactNode;
  noBorder?: boolean;
  /** Called after removeActivatedDeal() resolves — used by CartScreen to scroll to offers section */
  onRemoveComplete?: () => void;
};

export const OrderTotals = observer(({children, noBorder}: OrderTotalsProps) => {
  const styles = useStyles();
  const {cartStore, offersStore} = useStores();

  const {totalAmount} = cartStore;

  const totalChargedOnGiftCards = cartStore.totalChargedOnGiftCardsCents;
  const totalChargedOnCreditCards = cartStore.remainingBalanceAfterGiftCardsCents;

  const chargeLines = getChargeLines(cartStore.currentOrder?.order_lines);

  const renderTotalLine = (
    name: string,
    amount: number,
    index: number | string = 0,
    signed = false,
    strikethrough = false,
    // DIS-1608: hold the circular indicator on this line past the order write,
    // for the coupon re-check that follows it. Showing a figure the re-check is
    // about to change is the flicker.
    pending = false,
  ) => (
    <View
      key={`${name}_${index}`}
      style={styles.totalRow}
      testID={`checkout-screen-total-line${strikethrough ? '-strikethrough' : ''}`}>
      <Text
        style={[
          styles.totalName,
          styles.totalText,
          name?.indexOf('Total') === 0 && styles.totalBold,
          strikethrough && styles.strikethrough,
        ]}>
        {name}
      </Text>
      {cartStore.dirtyOrder || pending ? (
        <View style={styles.activityHolder}>
          <ActivityIndicator size="small" />
        </View>
      ) : (
        <Text
          style={[
            styles.totalAmount,
            styles.totalText,
            name?.indexOf('Total') === 0 && styles.totalBold,
            strikethrough && styles.strikethrough,
          ]}>
          {signed ? toAUD(amount, true) : toAUDAbs(amount, true)}
        </Text>
      )}
    </View>
  );

  // DIS-1608: one uncounted-discount computation over both arrays. An expired offer
  // and an unqualified coupon can co-occur, so the add-back is a single sum, not two.
  const expiredDealId = cartStore.activatedDeals[0];
  const expiredOfferHeadline =
    offersStore.data?.find((o: {mflId?: string}) => !!o.mflId && o.mflId === expiredDealId)
      ?.tileHeadline ?? null;
  const expiredOfferName = cartStore.expiredOfferDetected
    ? (expiredOfferHeadline?.toLowerCase() ?? null)
    : null;
  const uncountedCouponCode = cartStore.uncountedCouponCode;
  // DIS-1608: the code as attached, regardless of verdict. uncountedCouponCode is
  // null while a qualified coupon is being re-checked, so it cannot locate the line.
  const attachedCouponCode = cartStore.currentOrder?.coupon_code ?? null;
  const couponChecking = cartStore.couponStatus === 'checking';
  const uncountedCents = sumUncountedDiscounts({
    adjustments: cartStore.currentOrder?.adjustments,
    discounts: cartStore.currentOrder?.discounts_and_credits,
    expiredOfferName,
    uncountedCouponCode,
  });

  const content = (
    <>
      <Text style={{marginBottom: 8}} color="black" variant="heading3">
        Your cart total
      </Text>
      {renderTotalLine('Subtotal', calculateSubtotal(cartStore.currentOrder?.order_lines))}

      {renderTotalLine('GST included', cartStore.currentOrder?.total?.cents_tax || 0)}

      {/* Gift cards / store credits */}
      {!!totalChargedOnGiftCards && renderTotalLine('Gift cards', totalChargedOnGiftCards)}

      {/* Credit/Debit card, when gift card is used, the remaining amount is charged to the credit/debit card */}
      {/* DIS-1608: + uncountedCents so this line and 'Gift cards' still sum to
          'Total payment due'. The gift-card balance applied is fixed, so an
          uncounted coupon or expired offer falls entirely on the card. */}
      {!!totalChargedOnGiftCards &&
        renderTotalLine('Credit/Debit card', totalChargedOnCreditCards + uncountedCents)}

      {/* Free Pickup / Delivery Fee */}
      {!cartStore.switchingDeliveryPickup && (
        <>
          {chargeLines && chargeLines.length > 0
            ? chargeLines.map((ol: OrderLine, index: number) =>
                renderTotalLine(ol.name, getChargeLineAmount(ol), index),
              )
            : // Fallback: Show "Free Pickup" or "Delivery Fee" when no charge line from API
              cartStore.isPickup
              ? renderTotalLine('Free Pickup', 0)
              : cartStore.isDelivery
                ? renderTotalLine('Delivery Fee', 0)
                : null}
        </>
      )}

      {(() => {
        if (!cartStore.currentOrder?.adjustments) return null;
        // DIS-1220: scope strikethrough to the expired offer line only —
        // coupons and other adjustments must render normally.
        return cartStore.currentOrder.adjustments
          ?.filter(
            (adj: {name?: string; amount: {cents_inc_tax: number}}) =>
              !!adj.name && !adj.name.toLowerCase().includes('delivery'),
          )
          ?.map((adj: {name: string; amount: {cents_inc_tax: number}}, index: number) =>
            renderTotalLine(
              adj.name,
              adj.amount.cents_inc_tax,
              `${adj.name}_${index}`,
              true,
              isUncountedOfferAdjustment(adj, expiredOfferName),
            ),
          );
      })()}

      {/* Expired offer warning banner — use offersStore for accurate offer name */}
      {cartStore.expiredOfferDetected ? (
        <ExpiredOfferBanner narrative={expiredOfferHeadline || 'your exclusive offer'} />
      ) : null}

      {/* Discounts and Credits */}
      {cartStore.currentOrder?.discounts_and_credits &&
      cartStore.currentOrder?.discounts_and_credits.length > 0
        ? cartStore.currentOrder?.discounts_and_credits?.map(
            (dac: {narrative: string; amount: number}, index: number) =>
              renderTotalLine(
                dac.narrative,
                -1 * dac.amount,
                index,
                true,
                isUncountedCouponLine(dac, uncountedCouponCode),
                couponChecking && isUncountedCouponLine(dac, attachedCouponCode),
              ),
          )
        : null}

      {renderTotalLine(
        'Total payment due',
        totalAmount + uncountedCents,
        0,
        false,
        false,
        couponChecking,
      )}
      {children}
    </>
  );

  // DIS-1608: first paint, before loadOrderForUser resolves. Every figure below
  // would be zero, and a zero total that then jumps is worse than no total. Once
  // an order exists the per-line circular indicators take over — adding to cart
  // never clears currentOrder, so the skeleton does not reappear mid-session.
  if (!cartStore.currentOrder && cartStore.isLoadingOrder) {
    return (
      <View testID="checkout-screen-totals-skeleton">
        <CartSummarySkeleton noBorder={noBorder} />
      </View>
    );
  }

  if (noBorder) {
    return <View testID="checkout-screen-totals">{content}</View>;
  }

  return (
    <Card
      noMargin
      variant="outlined"
      containerStyle={styles.section}
      testID="checkout-screen-totals">
      {content}
    </Card>
  );
});

export default OrderTotals;

const useStyles = makeStyles(() => ({
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  webDesktopSectionTitle: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  totalBold: {fontWeight: 600},
  strikethrough: {
    textDecorationLine: 'line-through',
    color: '#6B7280', // Gray-500, muted text for strikethrough
    opacity: 0.7,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
    marginBottom: 8,
  },
  totalText: {
    color: 'black',
    fontSize: 16, // design is 14px but its too small
    // marginBottom: 8,
    // fontWeight: '500',
  },
  totalName: {
    flex: 3,
  },
  totalAmount: {
    flex: 1,
    textAlign: 'right',
  },
  activityHolder: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  cartItemSpacing: {
    marginBottom: 8,
  },
  rowCentered: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  bfButton: {
    marginVertical: 16,
  },
  label: {
    color: 'grey',
    fontSize: 12,
    marginBottom: 4,
  },
  section: {
    padding: 10,
  },
  noBorderSection: {
    padding: 0,
  },
  placeOrderButton: {
    backgroundColor: theme.colour.background.button.primary,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 4,
    alignItems: 'center',
    marginTop: 8,
  },
  placeOrderButtonText: {
    color: 'white',
    fontWeight: '600',
  },
  buttonActivity: {
    position: 'absolute',
    marginLeft: 8,
  },
  confirmText: {
    marginVertical: 8,
    color: theme.colour.text.content.primary,
  },
}));
